import React, { useState, useEffect } from "react";
import axios from "axios";
import Menu from "../Menu";
import { useNavigate } from "react-router-dom";
import Switch from "../../components/Switch";
import useApiUrls from "../../useApiUrls";
import Pagination from "../components/Pagination";

const ThirdPartyHtmlList = () => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilter, setActiveFilter] = useState("");
  const [dataLimit, setDataLimit] = useState(100);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("success");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalItems, setTotalItems] = useState(0);

  const navigate = useNavigate();
  const { getUrlByOperation } = useApiUrls();

  const token = getCookie("token") || getCookie("authToken") || "";
  const userid = getCookie("userid") || getCookie("userId") || "1";

  const getHeaders = () => ({
    "accept": "text/plain",
    "UserId": userid,
    "X-Authorization": token,
    "Content-Type": "application/json-patch+json"
  });

  const showToast = (msg, type = "success") => {
    setMessage(msg);
    setMessageType(type);
    setTimeout(() => {
      setMessage("");
    }, 3000);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const searchUrl = getUrlByOperation("ThirdPartyHtmlSearch") || "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlOperation/Search";
      const response = await axios.post(
        searchUrl,
        {
          id: -1,
          createdip: "",
          modifiedip: "",
          applycreateddate: false,
          applymodifieddate: false,
          fromcreateddate: 0,
          endcreateddate: 0,
          frommodifieddate: 0,
          endmodifieddate: 0,
          active: activeFilter,
          name: searchTerm,
          ispageload: true,
          pagesize: dataLimit,
          currentpageno: count,
          withoutpagination: false,
          pagedirection: ""
        },
        { headers: getHeaders() }
      );

      if (response.data && Array.isArray(response.data.data)) {
        const rows = response.data.data;
        setData(rows);
        setHasMore(rows.length === Number(dataLimit));
        const approximateTotal = (count * Number(dataLimit)) + rows.length + (rows.length === Number(dataLimit) ? Number(dataLimit) : 0);
        setTotalItems(approximateTotal);
      } else {
        setData([]);
        setHasMore(false);
        setTotalItems(0);
      }
    } catch (error) {
      console.error("Error fetching Third Party HTML data:", error);
      showToast(error.response?.data?.errormsg || "Failed to load data", "danger");
      if (error.message === "relogin token expire" || error.response?.status === 401) {
        navigate("/login");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrlByOperation, token, userid, dataLimit, count, activeFilter]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCount(0);
    fetchData();
  };

  const handlePageChange = (page) => {
    setCount(page - 1);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setDataLimit(newItemsPerPage);
    setCount(0);
  };

  const totalPages = hasMore ? count + 2 : count + 1;

  const handleAddClick = () => {
    navigate("/add-thirdparty-html");
  };

  const handleUpdateClick = (id) => {
    navigate(`/update-thirdparty-html/${id}`);
  };

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!selectedItem) return;
    setIsDeleting(true);
    try {
      const deleteUrl = getUrlByOperation("ThirdPartyHtmlDelete") || "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlOperation/Delete";
      const response = await axios.post(
        deleteUrl,
        { id: selectedItem.id },
        { headers: getHeaders() }
      );

      if (response.data && (response.data.isresponse || response.data.responsestatus === "SUCCESS")) {
        showToast("Third Party HTML deleted successfully!", "success");
        setShowDeleteModal(false);
        fetchData();
      } else {
        showToast(response.data?.errormsg || "Failed to delete item", "danger");
      }
    } catch (error) {
      console.error("Error deleting item:", error);
      showToast(error.response?.data?.errormsg || "Failed to delete item", "danger");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggle = async (item) => {
    const activeUrl = getUrlByOperation("ThirdPartyHtmlActive") || "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlOperation/UpdateStatus/Active";
    const deactiveUrl = getUrlByOperation("ThirdPartyHtmlDeactive") || "https://api.dqr-display-tms.bonrix.in/api/ThirdPartyHtmlOperation/UpdateStatus/Deactive";
    const apiUrl = item.active ? deactiveUrl : activeUrl;

    try {
      await axios.post(
        apiUrl,
        { id: item.id },
        { headers: getHeaders() }
      );
      setData(data.map((d) => (d.id === item.id ? { ...d, active: !item.active } : d)));
      showToast(`Status updated to ${!item.active ? "Active" : "Inactive"}`, "success");
    } catch (error) {
      console.error("Error updating status:", error);
      showToast(error.response?.data?.errormsg || "Error updating status", "danger");
      if (error.message === "relogin token expire" || error.response?.status === 401) {
        navigate("/login");
      }
    }
  };

  return (
    <>
      <Menu />
      <div className="wrapper">
        <div className="content-wrapper">
          {/* Header */}
          <section className="content-header">
            <div className="container-fluid">
              <div className="d-flex justify-content-between align-items-center w-100">
                <h1 className="mb-0">Third Party HTML</h1>
                <div>
                  <button
                    type="button"
                    className="btn btn-info mr-2"
                    onClick={() => navigate("/thirdparty-html-allocation")}
                  >
                    <i className="fas fa-tasks mr-1"></i> Allocation
                  </button>
                  <button type="button" className="btn btn-success" onClick={handleAddClick}>
                    <i className="fas fa-plus mr-1"></i> Add Third Party HTML
                  </button>
                </div>
              </div>
            </div>
          </section>

          {/* Main Content */}
          <section className="content">
            <div className="container-fluid">
              <hr />

              {/* Filters */}
              <div className="card mb-3">
                <div className="card-body">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="row">
                      <div className="col-md-5 col-sm-6 mb-2">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search by name..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <div className="col-md-3 col-sm-4 mb-2">
                        <select
                          className="form-control"
                          value={activeFilter}
                          onChange={(e) => {
                            setActiveFilter(e.target.value);
                            setCount(0);
                          }}
                        >
                          <option value="">All Statuses</option>
                          <option value="true">Active</option>
                          <option value="false">Inactive</option>
                        </select>
                      </div>
                      <div className="col-md-4 col-sm-2 mb-2 d-flex">
                        <button type="submit" className="btn btn-primary mr-2">
                          <i className="fas fa-search mr-1"></i> Search
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => {
                            setSearchTerm("");
                            setActiveFilter("");
                            setCount(0);
                          }}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>

              {/* Table */}
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body table-responsive p-0">
                      <table className="table table-hover text-nowrap">
                        <thead>
                          <tr>
                            <th>Id</th>
                            <th>Name</th>
                            <th>URL</th>
                            <th>Remark</th>
                            <th>Active</th>
                            <th className="text-center">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                  <span className="sr-only">Loading...</span>
                                </div>
                              </td>
                            </tr>
                          ) : data.length === 0 ? (
                            <tr>
                              <td colSpan="6" className="text-center py-4">
                                No records found.
                              </td>
                            </tr>
                          ) : (
                            data.map((item) => (
                              <tr key={item.id}>
                                <td>{item.id}</td>
                                <td><strong>{item.name || item.Firmwarename || "-"}</strong></td>
                                <td>
                                  {item.url ? (
                                    <a href={item.url} target="_blank" rel="noopener noreferrer" title={item.url}>
                                      {item.url.length > 40 ? `${item.url.substring(0, 40)}...` : item.url}
                                      <i className="fas fa-external-link-alt ml-1" style={{ fontSize: "0.80em" }}></i>
                                    </a>
                                  ) : (
                                    "-"
                                  )}
                                </td>
                                <td>{item.remark || "-"}</td>
                                <td>
                                  <Switch
                                    isOn={Boolean(item.active)}
                                    handleToggle={() => handleToggle(item)}
                                    id={item.id}
                                  />
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success mr-2"
                                    onClick={() => handleUpdateClick(item.id)}
                                    title="Edit"
                                  >
                                    <i className="fas fa-edit"></i> Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDeleteClick(item)}
                                    title="Delete"
                                  >
                                    <i className="fas fa-trash"></i> Delete
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      
                      <Pagination
                        currentPage={count + 1}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        itemsPerPage={dataLimit}
                        onItemsPerPageChange={handleItemsPerPageChange}
                        totalItems={totalItems}
                        showPageNumbers={false}
                        showItemsPerPageSelector={true}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header bg-danger text-white">
                <h5 className="modal-title">Confirm Delete</h5>
                <button type="button" className="close text-white" onClick={() => setShowDeleteModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body text-center">
                <p>Are you sure you want to delete <strong>{selectedItem?.name || `ID #${selectedItem?.id}`}</strong>?</p>
                <p className="text-muted"><small>This action cannot be undone.</small></p>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={isDeleting}>
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast Notification */}
      {message && (
        <div style={{ position: "fixed", top: "20px", right: "20px", width: "300px", zIndex: "9999" }}>
          <div className={`alert alert-${messageType} alert-dismissible fade show`} role="alert">
            {message}
            <button type="button" className="close" onClick={() => setMessage("")}>
              <span>&times;</span>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default ThirdPartyHtmlList;

function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
}
